import { Directive, HostBinding, HostListener, inject, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import moment, { Moment } from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MuiInputBaseDirective } from '../input/mui-input-base.directive';
import { MuiDateRange } from './mui-date-range';
import { MuiDatepicker } from './mui-datepicker.component';


let nextUniqueId = 0;

const datepickerComponentUndefinedError = () =>
  new Error(
    'MuiDatepickerInputDirective: MuiDatepickerComponent is undefined! ' +
    'Please bind a MuiDatepickerComponent to this directive via muiDatepickerInput input property.'
  );

@Directive({
  selector: 'input[muiDatepickerInput]'
})
export class MuiDatepickerInputDirective extends MuiInputBaseDirective<Moment> implements OnInit, OnDestroy {
  type = 'date';

  /* We want to bind to the directive directly */
  @Input('muiDatepickerInput') datepicker!: MuiDatepicker;
  @Input() dateRangeBinding?: 'from' | 'to';
  @Input() updateValue = true;

  @Input()
  @HostBinding('attr.autocomplete')
  private autocomplete = 'off';

  protected _uid = `mui-datepicker-input-${nextUniqueId++}`;
  private renderer: Renderer2 = inject(Renderer2);
  private _destroy$ = new Subject<void>();

  @HostListener('focus', ['$event'])
  onFocus(): void {
    this.datepicker.open();
  }

  @HostListener('input', ['$event'])
  onInput(e: Event): void {
    if (this.datepicker.range) {
      // ToDo: We need to implement proper parsing of range input!
      return;
    }
    const value = (e.target as HTMLInputElement).value;
    const date = moment(value, 'L');
    this.datepicker.writeValue(date.isValid() ? date : null);
    this.datepicker.setFormValue(date);
  }

  ngOnInit(): void {
    if (!this.datepicker) {
      throw datepickerComponentUndefinedError();
    }

    this.datepicker.registerTriggerElement(this._elementRef.nativeElement);
    this._registerDatepickerSelectEvent();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _registerDatepickerSelectEvent(): void {
    this.datepicker.onSelect$.pipe(takeUntil(this._destroy$)).subscribe((d: Moment | MuiDateRange | null) => {
      if (!d) {
        this._renderValueInInput('');
        return;
      }

      if (moment.isMoment(d)) {
        this._renderValueInInput(d.format('L'));
      } else if (this.dateRangeBinding === 'from') {
        this._renderValueInInput(d.fromDate.format('L'));
      } else if (this.dateRangeBinding === 'to') {
        this._renderValueInInput(d.toDate ? d.toDate.format('L') : '');
      } else if (d.fromDate && !d.toDate) {
        this._renderValueInInput(`${d.fromDate.format('L')} - `);
      } else if (d.fromDate && d.toDate) {
        this._renderValueInInput(`${d.fromDate.format('L')} - ${d.toDate.format('L')}`);
      }
    });
  }

  private _renderValueInInput(value: string): void {
    if (this.updateValue) {
      this.renderer.setProperty(this._elementRef.nativeElement, 'value', value);
    }
  }
}
